import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { featureAccessGuard, roleAccessGuard, SsoTriggerComponent } from '@nk/access-block';
import { ReportContainerComponent } from '@nk/company/company-view-report/report-container/report-container.component';
import { ForceLogoutComponent } from '@nk/core/components/force-logout/force-logout.component';
import { LogoutViewComponent } from '@nk/core/components/logout-view/logout-view.component';
import { CallbackCorsAuthComponent } from '@nk/core/onboarding/callback-cors-auth/callback-cors-auth.component';
import { IdentityViewComponent } from '@nk/core/onboarding/identity-view/identity-view.component';
import { SignUpAccountComponent } from '@nk/core/onboarding/sign-up/components/sign-up-account/sign-up-account.component';
import { SignUpCompanyComponent } from '@nk/core/onboarding/sign-up/components/sign-up-company/sign-up-company.component';
import { SignUpExistingComponent } from '@nk/core/onboarding/sign-up/components/sign-up-existing/sign-up-existing.component';
import { SignUpSuccessComponent } from '@nk/core/onboarding/sign-up/components/sign-up-success/sign-up-success.component';
import { SignUpTosPrivacyComponent } from '@nk/core/onboarding/sign-up/components/sign-up-tos-privacy/sign-up-tos-privacy.component';
import { SignUpComponent } from '@nk/core/onboarding/sign-up/sign-up.component';
import { AdminGuardService } from '@nk/core/services/admin-guard.service';
import { IdentityGuardService } from '@nk/core/services/identity-guard.service';
import { IdentityOverrideComponent } from '@nk/core/views/identity-override/identity-override.component';
import { LoginViewComponent } from '@nk/core/views/login/login-view.component';
import { MaintenanceViewComponent } from '@nk/core/views/maintenance/maintenance-view.component';
import { PageForbiddenViewComponent } from '@nk/core/views/page-forbidden-view/page-forbidden-view.component';
import { AppFeatureKey, AppRoleKey } from '@nk/data-entities-lib';
import { PageNotFoundViewComponent } from '@nk/ui-lib';
import { resolveTranslation } from '@nk/utils-lib';
import { AppLayoutComponent } from '@nk/web-portal-block';

// all routes that utilizes load children will be lazy loaded in.
export const ROUTES: Routes = [
  {
    path: 'admin',
    canActivate: [IdentityGuardService, AdminGuardService],
    title: () => resolveTranslation(extractTranslation('route.admin')),
    loadChildren: () => import('../../../../libs/common-block/src/lib/admin/admin.module').then((m) => m.AdminModule),
  },
  {
    path: 'sso',
    children: [
      {
        path: ':connection',
        component: SsoTriggerComponent,
      },
    ],
  },
  {
    path: 'eika',
    redirectTo: 'sso/enin-ad', // Eika uses microsoft azure ad
  },
  {
    path: 'report',
    canActivate: [IdentityGuardService],
    component: ReportContainerComponent,
    children: [
      {
        path: 'company',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/company/company-view-report/company-view-report.module').then(
            (m) => m.CompanyViewReportModule,
          ),
        title: () => resolveTranslation(extractTranslation('route.company')),
        data: { title: extractTranslation('route.company') as string },
      },
      {
        path: 'property',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.norwegian_property_data]),
          roleAccessGuard([AppRoleKey.norwegian_property_data]),
        ],
        loadChildren: () =>
          import(
            '../../../../libs/common-block/src/lib/property/property-view-report/property-view-report.module'
          ).then((m) => m.PropertyViewReportModule),
        title: () => resolveTranslation(extractTranslation('route.property')),
        data: { title: extractTranslation('route.property') as string },
      },
      {
        path: 'screening/:screeningConfigUuid/:monitoringOriginEntityUuid',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.screening]),
          roleAccessGuard([AppRoleKey.aml]),
        ],
        loadComponent: () => import('@nk/screening-block').then((m) => m.ScreeningReportViewComponent),
        title: () => resolveTranslation(extractTranslation('route.screening')),
        data: { title: extractTranslation('route.screening') as string },
      },
      {
        // Deprecated route, to be removed in the future
        path: 'screening/:screeningConfigUuid/screening-case/:screeningCaseUuid',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.screening]),
          roleAccessGuard([AppRoleKey.aml]),
        ],
        loadComponent: () => import('@nk/screening-block').then((m) => m.ScreeningCaseReportViewComponent),
        title: () => resolveTranslation(extractTranslation('route.screening_case')),
        data: { title: extractTranslation('route.screening_case') as string },
      },
      {
        path: 'screening/:screeningConfigUuid/screening-entity/:screeningEntityUuid',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.screening]),
          roleAccessGuard([AppRoleKey.aml]),
        ],
        loadComponent: () => import('@nk/screening-block').then((m) => m.ScreeningCaseReportViewComponent),
        title: () => resolveTranslation(extractTranslation('route.screening_case')),
        data: { title: extractTranslation('route.screening_case') as string },
      },
    ],
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [IdentityGuardService],
    data: {
      title: 'Enin',
      noBreadcrumb: true,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('@nk/dashboard-block').then((m) => m.DashboardViewComponent),
        title: () => resolveTranslation(extractTranslation('route.dashboard')),
        data: {
          title: 'Enin',
          noBreadcrumb: true,
        },
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/settings/settings.module').then((m) => m.SettingsModule),
        title: () => resolveTranslation(extractTranslation('route.settings')),
      },
      {
        path: 'files',
        canActivate: [featureAccessGuard([AppFeatureKey.base, AppFeatureKey.custom_company_report])],
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/file-manager/file-manager.module').then(
            (m) => m.FileManagerModule,
          ),
        title: () => resolveTranslation(extractTranslation('route.file_manager')),
        data: { title: extractTranslation('route.file_manager') },
      },
      {
        path: 'dashboard',
        redirectTo: '',
        data: {
          noTabs: true,
          hideAllBreadcrumbs: true,
        },
      },
      {
        path: 'risk',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () => import('../../../../libs/common-block/src/lib/risk/risk.module').then((m) => m.RiskModule),
        title: () => resolveTranslation(extractTranslation('route.event')),
        data: { title: extractTranslation('route.event') as string, hideAllBreadcrumbs: true },
      },
      {
        path: 'watchlist',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/watchlist/watchlist.module').then((m) => m.WatchlistModule),
        title: () => resolveTranslation(extractTranslation('route.watchlist')),
        data: { title: extractTranslation('route.watchlist') as string },
      },
      {
        path: 'digest',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/digest/digest.module').then((m) => m.DigestModule),
        title: () => resolveTranslation(extractTranslation('route.digest')),
        data: {
          title: extractTranslation('route.digest') as string,
        },
      },
      {
        path: 'company',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/company/company.module').then((m) => m.CompanyModule),
        title: () => resolveTranslation(extractTranslation('route.company_browser')),
        data: {
          title: extractTranslation('route.company') as string,
        },
      },
      {
        path: 'person',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () =>
          import('../../../../libs/common-block/src/lib/person/person.module').then((m) => m.PersonModule),
        title: () => resolveTranslation(extractTranslation('route.person')),
        data: { title: extractTranslation('route.person') as string },
      },
      // Hidden until we potentially fix issues with it. Dated 08 feb 2024
      // {
      //   path: 'collateral',
      //   canActivate: [featureAccessGuard([AppFeatureKey.base, AppFeatureKey.collateral])],
      //   loadChildren: () =>
      //     import('../../../../libs/common-block/src/lib/collateral/collateral.module').then((m) => m.CollateralModule),
      //   data: { title: extractTranslation('route.collateral') as string },
      // },
      {
        path: 'search',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () => import('@nk/company-person-search-block').then((m) => m.COMPANY_PERSON_SEARCH_ROUTES),
        title: () => resolveTranslation(extractTranslation('route.search')),
        data: { title: extractTranslation('route.search') as string },
      },
      {
        path: 'screening',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.screening]),
          roleAccessGuard([AppRoleKey.aml]),
        ],
        loadChildren: () => import('@nk/screening-block').then((m) => m.SCREENING_ROUTES),
        title: () => resolveTranslation(extractTranslation('route.screening')),
        data: { title: extractTranslation('route.screening') as string },
      },
      {
        path: 'property',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.norwegian_property_data]),
          roleAccessGuard([AppRoleKey.norwegian_property_data]),
        ],
        loadChildren: () =>
          import('@nk/app/property/property-view/property-view.module').then((m) => m.PropertyViewModule),
        title: () => resolveTranslation(extractTranslation('route.property')),
        data: { title: extractTranslation('route.property') as string },
      },
      {
        path: 'address',
        canActivate: [featureAccessGuard([AppFeatureKey.base])],
        loadChildren: () => import('@nk/app/address/address-view/address-view.module').then((m) => m.AddressViewModule),
        title: () => resolveTranslation(extractTranslation('route.address')),
        data: { title: extractTranslation('route.address') as string },
      },
      {
        path: 'bank-account-prevalidation',
        canActivate: [
          featureAccessGuard([AppFeatureKey.base, AppFeatureKey.bank_account_prevalidation]),
          roleAccessGuard([AppRoleKey.bank_account_prevalidation]),
        ],
        loadChildren: () =>
          import('@nk/app/bank-account-prevalidation/bank-account-prevalidation.module').then(
            (m) => m.BankAccountPrevalidationModule,
          ),
        title: () => resolveTranslation(extractTranslation('route.bank_account_prevalidation')),
        data: { title: extractTranslation('route.bank_account_prevalidation') as string },
      },
    ],
  },
  {
    path: 'login',
    component: LoginViewComponent,
    title: 'Login',
    data: { title: 'Login' },
  },
  {
    path: 'force-logout',
    component: ForceLogoutComponent,
    title: 'Force logout',
    data: { title: 'Force logout' },
  },
  {
    path: 'logout-success',
    component: LogoutViewComponent,
    title: 'Logout success',
    data: { title: 'Logout success' },
  },
  {
    path: 'verification-success',
    loadComponent: () => import('@nk/onboarding-block').then((m) => m.VerificationSuccessViewComponent),
  },
  {
    path: 'maintenance',
    component: MaintenanceViewComponent,
    title: 'Maintenance',
  },
  {
    path: 'callback-cors-auth',
    component: CallbackCorsAuthComponent,
  },
  {
    path: 'sign-up',
    component: SignUpComponent,
    children: [
      { path: '', redirectTo: 'company', pathMatch: 'full' },
      { path: 'company', component: SignUpCompanyComponent },
      { path: 'account', component: SignUpAccountComponent },
      { path: 'tos-privacy', component: SignUpTosPrivacyComponent },
    ],
  },
  {
    path: 'sign-up-success',
    component: SignUpSuccessComponent,
  },
  {
    path: 'sign-up-existing',
    component: SignUpExistingComponent,
  },
  {
    path: 'invitation/:uuid',
    loadComponent: () => import('@nk/onboarding-block').then((m) => m.InvitationViewComponent),
  },
  {
    path: 'identity',
    component: IdentityViewComponent,
  },
  {
    path: 'identity-override',
    component: IdentityOverrideComponent,
  },
  {
    path: 'digest-unsubscribe',
    loadChildren: () =>
      import('../../../../libs/common-block/src/lib/digest-unsubscribe/digest-unsubscribe.module').then(
        (m) => m.DigestUnsubscribeModule,
      ),
  },
  {
    path: '403',
    component: PageForbiddenViewComponent,
  },
  {
    path: '404',
    component: PageNotFoundViewComponent,
  },
  {
    path: '**',
    component: PageNotFoundViewComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(ROUTES, {
      useHash: false,
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
